import Buttons from '@components/Buttons';
import CustomLink from '@components/CustomLink';
import TabIcon from '@components/DesignLibrary/Atoms/TabIcon';
import List from '@components/Icons/List';
import Noodle from '@components/Icons/Noodle';
import X from '@components/Icons/X';
import UserImage from '@components/UserImage';
import Image from 'next/image';
import { isAttorney } from '@helpers/businessCategories';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import { useRouter } from 'next/router';
import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import { DEPLOYMENT_TIER } from '@/configuration/client';
import TeamsContext from '@providers/Teams/TeamsContext';
import { Moon, Sun } from '@phosphor-icons/react';

import s from './DashboardNavigation.module.scss';

type TABS =
  | 'account'
  | 'inbox'
  | 'building-blocks'
  | 'home'
  | 'members'
  | 'messaging'
  | 'none'
  | 'services'
  | 'content'
  | 'team'
  | 'transactions'
  | 'scripts'
  | 'bookings'
  | 'workflows';

const PAGES: Array<{
  key: Exclude<TABS, 'team' | 'scripts' | 'none'>;
  to: string;
}> = [
  {
    key: 'home',
    to: getUrl(IDENTIFIERS.DASHBOARD),
  },
  {
    key: 'inbox',
    to: getUrl(IDENTIFIERS.DASHBOARD_INBOX),
  },
  {
    key: 'messaging',
    to: getUrl(IDENTIFIERS.DASHBOARD_CONVERSATIONS),
  },
  {
    key: 'workflows',
    to: getUrl(IDENTIFIERS.DASHBOARD_WORKFLOWS),
  },
  {
    key: 'building-blocks',
    to: getUrl(IDENTIFIERS.DASHBOARD_TERMS_TEMPLATES),
  },
  {
    key: 'services',
    to: getUrl(IDENTIFIERS.DASHBOARD_SERVICES),
  },
  {
    key: 'content',
    to: getUrl(IDENTIFIERS.DASHBOARD_CONTENT),
  },
  {
    key: 'members',
    to: getUrl(IDENTIFIERS.DASHBOARD_MEMBERS),
  },
  {
    key: 'transactions',
    to: getUrl(IDENTIFIERS.DASHBOARD_TRANSACTIONS),
  },
  {
    key: 'bookings',
    to: getUrl(IDENTIFIERS.DASHBOARD_BOOKINGS),
  },
];

const DashboardNavigation: FC<{
  avatar: {
    color?: string | null;
    name?: string | null;
    image?: string | null;
  };
  logo?: string;
  hasLeftNav: boolean;
  setIsSupportModalOpen: (isOpen: boolean) => void;
  currentTab: TABS;
  unreadInboxItemCount?: number;
}> = ({ avatar, currentTab, hasLeftNav, unreadInboxItemCount = 0, logo }) => {
  const [darkMode, setDarkMode] = useState(false);
  const router = useRouter();
  const { creator, isUserModalOpen, setIsUserModalOpen } = useContext(DashboardContext);
  const team = useContext(TeamsContext);
  const [isOpen, setOpen] = useState(hasLeftNav);
  const canShowInbox = useFeatureFlag('can-use-inbox');

  const applyColorScheme = (isDark: boolean): void => {
    const root = document.documentElement;
    if (isDark) {
      root.style.setProperty('--color-gray-100', '#f0f0f0');
      root.style.setProperty('--color-gray-75', '#aaaaaa');
      root.style.setProperty('--color-gray-50', '#666666');
      root.style.setProperty('--color-gray-25', '#333333');
      root.style.setProperty('--color-gray-15', '#292929');
      root.style.setProperty('--color-gray-0', '#212121');
      root.style.setProperty('--color-error', '#e85160');
      root.style.setProperty(
        '--ai-bg-100',
        `conic-gradient(
          from 140deg at 50% 50%,
          rgba(239, 153, 1, 1) 0deg,
          rgba(50, 50, 50, 1) 120deg,
          rgba(50, 184, 249, 1) 230deg,
          rgba(50, 50, 50, 1) 300deg,
          rgba(239, 153, 1, 1) 360deg
        )`,
      );
      root.style.setProperty(
        '--ai-bg-20',
        `linear-gradient(22.82deg, rgba(50, 50, 50, 0.82) 1%, rgba(0, 0, 102, 0) 100%),
          linear-gradient(247.13deg, #212121 0%, rgba(0, 0, 102, 0) 100%),
          linear-gradient(158.35deg, rgba(255, 0, 85, 0) 0%, rgba(255, 222, 167, 0.99) 100%),
          conic-gradient(from 189.21deg at 0% 0%, #a809f6 22%, rgba(0, 163, 255, 0.99) 66%, #f60974 100%)`,
      );
    } else {
      root.style.removeProperty('--color-gray-100');
      root.style.removeProperty('--color-gray-75');
      root.style.removeProperty('--color-gray-50');
      root.style.removeProperty('--color-gray-25');
      root.style.removeProperty('--color-gray-15');
      root.style.removeProperty('--color-gray-0');
      root.style.removeProperty('--color-error');
      root.style.removeProperty('--ai-bg-100');
      root.style.removeProperty('--ai-bg-20');
    }
  };

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('dashboard-dark-mode') === 'true';
    setDarkMode(savedDarkMode);
    applyColorScheme(savedDarkMode);
    return () => {
      const root = document.documentElement;
      root.style.removeProperty('--color-gray-100');
      root.style.removeProperty('--color-gray-75');
      root.style.removeProperty('--color-gray-50');
      root.style.removeProperty('--color-gray-25');
      root.style.removeProperty('--color-gray-15');
      root.style.removeProperty('--color-gray-0');
      root.style.removeProperty('--color-error');
      root.style.removeProperty('--ai-bg-100');
      root.style.removeProperty('--ai-bg-20');
    };
  }, []);

  useEffect(() => {
    if (!hasLeftNav) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [router.pathname, isUserModalOpen, hasLeftNav]);

  useEffect(() => {
    if (!hasLeftNav && isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [hasLeftNav, isOpen]);

  const toggleDarkMode = (): void => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('dashboard-dark-mode', String(newDarkMode));
    applyColorScheme(newDarkMode);
  };

  return (
    <>
      <nav
        className={s.nav}
        {...((hasLeftNav && {
          style: {
            height: 'min(100vh, 100dvh)',
          },
        }) || (isOpen && !hasLeftNav && {
          style: {
            bottom: 0,
            left: 0,
            position: 'fixed',
            right: 0,
            top: 0,
          },
        }))}
      >
        {!hasLeftNav && (
          <div className={s.header}>
            <Buttons
              isWrapper
              onClick={() => setOpen(!isOpen)}
              className={s.menu}
              {...(isOpen && {
                style: {
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-gray-0)',
                },
              })}
            >
              {isOpen ? <X size={20} /> : <List size={20} />}
            </Buttons>
            <div>
              <UserImage size={24} name={avatar.name || null} url={avatar.image} color={avatar.color || null} />
              {avatar.name}
            </div>
            <div />
          </div>
        )}
        {isOpen && (
          <div className={!hasLeftNav ? s.firstLevelMobile : s.firstLevel}>
            {hasLeftNav && (
              <CustomLink to={getUrl(IDENTIFIERS.DASHBOARD)} className={s.logo} prefetch={false}>
                <Noodle fill={DEPLOYMENT_TIER === 'production' ? 'var(--color-noodle)' : 'var(--color-warning)'} width={32} height={32} />
              </CustomLink>
            )}
            <div className={!hasLeftNav ? s.tabsMobile : s.tabs}>
              {PAGES.map(page => {
                const isActive = page.key === currentTab;
                const hideContentTab = page.key === 'content' && isAttorney(creator?.businessCategory);
                const hideInboxTab = page.key === 'inbox' && !canShowInbox;
                const hideTransactionsTab = page.key === 'transactions' && !team.role.finance;
                if (hideContentTab || hideInboxTab || hideTransactionsTab) {
                  return null;
                }
                return (
                  <TabIcon
                    showLabel={!hasLeftNav}
                    key={page.key}
                    icon={page.key}
                    href={page.to}
                    selected={isActive}
                    notificationCount={page.key === 'inbox' ? unreadInboxItemCount : undefined}
                  />
                );
              })}
            </div>
            <div className={!hasLeftNav ? s.bottomMobile : s.bottom}>
              {hasLeftNav && (
                <>
                  {darkMode ? <Moon weight="fill" color="var(--color-gray-75)" /> : <Sun weight="fill" color="var(--color-gray-75)" />}
                  <label className={s.darkModeToggle}>
                    <div />
                    <input type="checkbox" checked={darkMode} onChange={toggleDarkMode} />
                  </label>
                </>
              )}
              {!hasLeftNav
                ? (
                  <TabIcon selected={currentTab === 'account'} showLabel icon="account" onClick={() => setIsUserModalOpen(!isUserModalOpen)} />
                )
                : (
                  <Buttons isWrapper onClick={() => setIsUserModalOpen(!isUserModalOpen)} aria-label="Your Profile">
                    {logo && (
                      <div className={s.businessLogo}>
                        <Image alt="" src={logo} width={40} height={40} />
                      </div>
                    )}
                    <UserImage
                      name={avatar.name}
                      color={avatar.color}
                      size={24}
                      {...((currentTab === 'account' && { className: s.accountSelected }) || {})}
                    />{' '}
                    {!hasLeftNav && 'Your Account'}
                  </Buttons>
                )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default DashboardNavigation;
